import { useEffect, useMemo, useRef, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import socket, { getRequest, postRequest, showMessage } from '../../../config';

const SOCKET_EVENT = 'permissionMigration:event';

const PAGE_SIZE = 20;

const STATUS_COLORS = { updated: '#09B255', skipped: '#AAACB5', error: '#F0606C', running: '#4A67FF' };

const LEVEL_COLORS = { info: '#5E5F66', success: '#09B255', warn: '#F0AE0D', error: '#F0606C', running: '#4A67FF' };

const StatTile = ({ label, value, color }) => (
    <div className="df fdc aic jcc br10px" style={{ background: '#F7F8FE', border: '1px solid #DDE2F2', padding: '10px 14px', minWidth: 70, flex: 1 }}>
        <span className="fs1-25rem fw600" style={{ color }}>{value ?? '-'}</span>
        <span className="fs11px fw400 c28 tac wsn">{label}</span>
    </div>
);

const ProgressBar = ({ current, total }) => {
    const pct = total > 0 ? Math.min(100, Math.round((current / total) * 100)) : 0;
    return (
        <div>
            <div className="df aic jcsb mb0-5rem">
                <span className="fs12px fw500 c15">{current} / {total} companies</span>
                <span className="fs12px fw500" style={{ color: '#4A67FF' }}>{pct}%</span>
            </div>
            <div style={{ height: 8, background: '#E8EBF5', borderRadius: 99, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${pct}%`, background: '#4A67FF', borderRadius: 99, transition: 'width 400ms ease' }} />
            </div>
        </div>
    );
};

const formatTime = (ts) => new Date(ts || Date.now()).toLocaleTimeString('en-GB', { hour12: false });

export default function CompanyWiseMigration() {
    const [companies, setCompanies] = useState([]);
    const [total, setTotal] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState([]);
    const [loadingCompanies, setLoadingCompanies] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [dryRun, setDryRun] = useState(true);
    const [running, setRunning] = useState(false);
    const [logs, setLogs] = useState([]);
    const [rows, setRows] = useState([]);
    const [progress, setProgress] = useState({ current: 0, total: 0 });
    const [summary, setSummary] = useState(null);

    const runIdRef = useRef(null);
    const logEndRef = useRef(null);
    const searchRef = useRef('');
    const loadedCountRef = useRef(0);
    const dryRunRef = useRef(dryRun);

    useEffect(() => { dryRunRef.current = dryRun; }, [dryRun]);

    // Reason: the picker is server-driven — already-migrated companies must never reach the client,
    // so both the initial list and every search hit the API instead of filtering in memory.
    const loadCompanies = async (term = '', skip = 0) => {
        const isFirstPage = skip === 0;
        if (isFirstPage) setLoadingCompanies(true); else setLoadingMore(true);

        const params = new URLSearchParams({ limit: String(PAGE_SIZE), skip: String(skip) });
        if (term) params.set('search', term);
        const res = await getRequest(`/modules/permission-migration/companies?${params.toString()}`);

        // Drop responses for a search term the user has already typed past
        if (searchRef.current === term && res?.code === 200) {
            const body = res['body'] || {};
            const list = body['companies'] || [];
            setCompanies(prev => {
                const next = isFirstPage ? list : [...prev, ...list];
                loadedCountRef.current = next.length;
                return next;
            });
            setTotal(body['total'] || 0);
            setHasMore(!!body['hasMore']);
        }

        if (isFirstPage) setLoadingCompanies(false); else setLoadingMore(false);
    };

    useEffect(() => {
        searchRef.current = search;
        const timer = setTimeout(() => loadCompanies(search, 0), 350);
        return () => clearTimeout(timer);
    }, [search]);

    const handleListboxScroll = (event) => {
        const node = event.currentTarget;
        if (loadingMore || loadingCompanies || !hasMore) return;
        if (node.scrollTop + node.clientHeight >= node.scrollHeight - 24) loadCompanies(searchRef.current, loadedCountRef.current);
    };

    const appendLog = (level, text) => setLogs(prev => [...prev, { at: Date.now(), level, text }]);

    // Reason: the migration runs in the background on the server — all progress arrives over this socket event.
    useEffect(() => {
        if (!socket) return;

        const onEvent = (payload) => {
            if (!payload || payload['runId'] !== runIdRef.current) return;
            const type = payload['type'];

            if (type === 'init') appendLog('info', payload['message']);

            if (type === 'modules-loaded') appendLog('info', `Loaded ${payload['moduleCount']} active modules and ${payload['sectionCount']} sections (${payload['uniqueSectionNos']} unique section numbers)`);

            if (type === 'companies-loaded') {
                setProgress({ current: 0, total: payload['totalCompanies'] });
                appendLog('info', `${payload['totalCompanies']} companies queued for migration`);
            }

            if (type === 'company-start') {
                setProgress({ current: payload['index'] - 1, total: payload['total'] });
                setRows(prev => {
                    const next = prev.filter(r => r['companyId'] !== payload['companyId']);
                    return [...next, { companyId: payload['companyId'], cName: payload['cName'], index: payload['index'], status: 'running' }];
                });
                appendLog('running', `[${payload['index']}/${payload['total']}] Processing ${payload['cName'] || payload['companyId']}…`);
            }

            if (type === 'company-done') {
                setProgress({ current: payload['index'], total: payload['total'] });
                setRows(prev => prev.map(r => r['companyId'] === payload['companyId']
                    ? { ...r, status: payload['status'], message: payload['message'], stats: payload['stats'], testType: payload['testType'], subscription: payload['subscription'] }
                    : r));
                const stats = payload['stats'];
                const detail = stats
                    ? ` — advisors ${stats['advisors']}, revenue lead ${stats['revenuelead']}, investors ${stats['investors']}, members ${stats['members']}`
                    : '';
                const level = payload['status'] === 'error' ? 'error' : payload['status'] === 'skipped' ? 'warn' : 'success';
                appendLog(level, `[${payload['index']}/${payload['total']}] ${payload['cName'] || payload['companyId']} — ${payload['message']}${detail}`);
            }

            if (type === 'complete') {
                setSummary(payload['summary']);
                setRunning(false);
                runIdRef.current = null;
                appendLog('success', `Migration finished in ${Math.round((payload['durationMs'] || 0) / 1000)}s`);
                showMessage('Permission migration completed.');
                // A live run flags the companies as migrated server side — drop them from the picker.
                if (!dryRunRef.current) {
                    setSelected([]);
                    loadCompanies(searchRef.current, 0);
                }
            }

            if (type === 'failed') {
                setRunning(false);
                runIdRef.current = null;
                appendLog('error', `Migration failed: ${payload['message']}`);
                showMessage(payload['message'] || 'Permission migration failed.', 'error');
            }
        };

        socket.on(SOCKET_EVENT, onEvent);
        return () => socket.off(SOCKET_EVENT, onEvent);
    }, []);

    useEffect(() => { logEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [logs]);

    const handleRun = async () => {
        if (!selected.length) return showMessage('Please select at least one company.', 'error');
        setLogs([]);
        setRows([]);
        setSummary(null);
        setProgress({ current: 0, total: selected.length });
        setRunning(true);

        const res = await postRequest('/modules/run-permission-migration', {
            companyIds: selected.map(c => c['_id']),
            dryRun,
            stream: true,
        }, true);

        if (res?.code === 200) {
            runIdRef.current = res['body']?.['runId'];
            appendLog('info', `Run ${runIdRef.current} started for ${selected.length} compan${selected.length === 1 ? 'y' : 'ies'} (${dryRun ? 'dry run' : 'live run'})`);
        } else {
            setRunning(false);
            appendLog('error', res?.message || 'Failed to start migration.');
            showMessage(res?.message || 'Failed to start migration.', 'error');
        }
    };

    // Reason: MUI warns when a selected value is missing from options — searching swaps the page out,
    // so already-picked companies are appended back in.
    const options = useMemo(() => {
        const loadedIds = new Set(companies.map(c => c['_id']));
        return [...companies, ...selected.filter(c => !loadedIds.has(c['_id']))];
    }, [companies, selected]);

    const counts = useMemo(() => ({
        updated: rows.filter(r => r['status'] === 'updated').length,
        skipped: rows.filter(r => r['status'] === 'skipped').length,
        errors: rows.filter(r => r['status'] === 'error').length,
    }), [rows]);

    const sortedRows = useMemo(() => [...rows].sort((a, b) => a['index'] - b['index']), [rows]);

    return (
        <div className="p2rem w100 df" style={{ gap: 32, alignItems: 'flex-start' }}>

            {/* ── LEFT: Controls ── */}
            <div style={{ flex: '0 0 440px', maxWidth: 440 }}>
                <h2 className="c6 fs1-5rem fw600 mb0-5rem">Company Wise Migration</h2>
                <p className="c28 fs14px fw400 mb1-5rem">
                    Search and select the companies whose advisor, investor, revenue lead and member permissions
                    should be rebuilt against the current TempModules and TempSections.
                </p>

                <label className="db c15 fs14px fw500 mb0-5rem">Companies</label>
                <Autocomplete
                    multiple
                    disableCloseOnSelect
                    limitTags={3}
                    options={options}
                    filterOptions={(x) => x}
                    value={selected}
                    disabled={running}
                    loading={loadingCompanies}
                    inputValue={search}
                    onInputChange={(e, value, reason) => { if (reason === 'input') setSearch(value); }}
                    onChange={(e, value) => setSelected(value)}
                    getOptionLabel={(option) => option['cName'] || option['_id']}
                    isOptionEqualToValue={(option, value) => option['_id'] === value['_id']}
                    noOptionsText={loadingCompanies ? 'Searching…' : 'No pending companies found'}
                    ListboxProps={{ onScroll: handleListboxScroll, style: { maxHeight: 320 } }}
                    renderOption={(props, option, { selected: isSelected }) => (
                        <li {...props} key={option['_id']}>
                            <Checkbox size="small" checked={isSelected} style={{ marginRight: 8, color: '#4A67FF' }} />
                            <div className="df fdc">
                                <span className="fs14px fw500 c15">{option['cName'] || 'Untitled company'}</span>
                                <span className="fs11px c28">{option['testType'] || 'investment'} · {option['subscription'] || 'Free'}</span>
                            </div>
                        </li>
                    )}
                    renderTags={(value, getTagProps) => value.map((option, index) => (
                        <Chip size="small" label={option['cName'] || option['_id']} {...getTagProps({ index })} key={option['_id']} />
                    ))}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            size="small"
                            placeholder="Search companies…"
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {(loadingCompanies || loadingMore) && <CircularProgress size={16} style={{ color: '#4A67FF' }} />}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                    sx={{ '& .MuiOutlinedInput-root': { background: '#F7F8FE', borderRadius: '10px' } }}
                />

                <div className="df aic jcsb mt0-75rem mb1-5rem">
                    <span className="fs12px c28">{selected.length} selected · {companies.length} of {total} pending loaded</span>
                    <div className="df aic" style={{ gap: 12 }}>
                        <button
                            onClick={() => setSelected(options)}
                            disabled={running || !companies.length}
                            style={{ background: 'none', border: 'none', padding: 0, cursor: running ? 'not-allowed' : 'pointer', color: '#4A67FF', fontSize: 13, fontWeight: 500 }}
                        >Select Loaded</button>
                        <button
                            onClick={() => setSelected([])}
                            disabled={running || !selected.length}
                            style={{ background: 'none', border: 'none', padding: 0, cursor: running ? 'not-allowed' : 'pointer', color: '#FC7785', fontSize: 13, fontWeight: 500 }}
                        >Clear</button>
                    </div>
                </div>

                <div
                    onClick={() => { if (!running) setDryRun(!dryRun); }}
                    className="df aic mb1-5rem"
                    style={{
                        padding: '12px 14px', borderRadius: 10, gap: 12, userSelect: 'none',
                        border: dryRun ? '2px solid #4A67FF' : '1px solid #DDE2F2',
                        background: dryRun ? '#F0F2FD' : '#F7F8FE',
                        cursor: running ? 'not-allowed' : 'pointer', opacity: running ? 0.6 : 1,
                    }}
                >
                    <div style={{
                        width: 20, height: 20, minWidth: 20, borderRadius: 5,
                        border: dryRun ? '2px solid #4A67FF' : '2px solid #D9D9D9',
                        background: dryRun ? '#4A67FF' : '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        {dryRun && (
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                <path d="M2 6L5 9L10 3" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        )}
                    </div>
                    <div>
                        <div className="fs14px fw500 c15">Dry run</div>
                        <div className="fs11px c28">Preview the changes without writing anything to the database</div>
                    </div>
                </div>

                <button
                    onClick={handleRun}
                    disabled={running || !selected.length}
                    style={{
                        padding: '12px 32px', borderRadius: 50, border: 'none',
                        background: (running || !selected.length) ? '#AAACB5' : dryRun ? '#4A67FF' : '#FC7785',
                        color: '#fff', fontSize: 14, fontWeight: 500,
                        cursor: (running || !selected.length) ? 'not-allowed' : 'pointer',
                        minWidth: 220, transition: 'background 200ms',
                    }}
                >
                    {running ? 'Migration Running…' : `${dryRun ? 'Preview' : 'Run'} Migration${selected.length ? ` (${selected.length})` : ''}`}
                </button>

                {(running || progress.total > 0) && (
                    <div className="mt1-5rem">
                        <ProgressBar current={progress.current} total={progress.total} />
                    </div>
                )}
            </div>

            {/* ── RIGHT: Live logs + results ── */}
            <div style={{ flex: 1, minWidth: 0 }}>
                {logs.length === 0 && !running ? (
                    <div className="df fdc aic jcc br10px" style={{ background: '#F7F8FE', border: '1px dashed #DDE2F2', padding: '3rem 2rem' }}>
                        <span className="fs14px fw500 c28 tac">Select companies and run the migration to see live progress here.</span>
                    </div>
                ) : (
                    <>
                        <div className="df aic jcsb mb1rem">
                            <h3 className="c15 fs1rem fw600 m0">Live Migration Log</h3>
                            <div className="df aic" style={{ gap: 8 }}>
                                <span className="fs11px fw600" style={{ background: '#F0FFF6', color: '#09B255', borderRadius: 50, padding: '3px 10px' }}>{counts.updated} updated</span>
                                <span className="fs11px fw600" style={{ background: '#F0F2FD', color: '#5E5F66', borderRadius: 50, padding: '3px 10px' }}>{counts.skipped} skipped</span>
                                <span className="fs11px fw600" style={{ background: '#FFF5F5', color: '#F0606C', borderRadius: 50, padding: '3px 10px' }}>{counts.errors} errors</span>
                            </div>
                        </div>

                        <div
                            className="br10px mb1-5rem"
                            style={{ background: '#0F1424', padding: '14px 16px', maxHeight: 280, overflowY: 'auto', fontFamily: 'monospace', fontSize: 12.5, lineHeight: 1.7 }}
                        >
                            {logs.map((log, i) => (
                                <div key={i} style={{ color: LEVEL_COLORS[log['level']] === '#5E5F66' ? '#AAACB5' : LEVEL_COLORS[log['level']] }}>
                                    <span style={{ color: '#5E5F66' }}>{formatTime(log['at'])}  </span>{log['text']}
                                </div>
                            ))}
                            <div ref={logEndRef} />
                        </div>

                        {summary && (
                            <div className="df fww mb1-5rem" style={{ gap: 8 }}>
                                <StatTile label="Total Cos." value={summary['totalCompanies']} color="#002D63" />
                                <StatTile label="Updated" value={summary['companiesWithChanges']} color="#09B255" />
                                <StatTile label="Skipped" value={summary['companiesSkipped']} color="#AAACB5" />
                                <StatTile label="Advisors" value={summary['totalAdvisorUsersUpdated']} color="#4A67FF" />
                                <StatTile label="Revenue Lead" value={summary['totalRevenueLeadUsersUpdated']} color="#038589" />
                                <StatTile label="Investors" value={summary['totalInvestorUsersUpdated']} color="#fb7a27" />
                                <StatTile label="Members" value={summary['totalMembersUpdated']} color="#0BA26F" />
                                <StatTile label="Errors" value={summary['errors']?.length ?? 0} color="#F0606C" />
                            </div>
                        )}

                        {sortedRows.length > 0 && (
                            <div style={{ borderRadius: 8, overflow: 'hidden', border: '1px solid #DDE2F2' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                    <thead>
                                        <tr style={{ background: '#F0F2FD' }}>
                                            <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#002D63', borderBottom: '1px solid #DDE2F2' }}>Company</th>
                                            <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, color: '#4A67FF', borderBottom: '1px solid #DDE2F2', whiteSpace: 'nowrap' }}>Advisors</th>
                                            <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, color: '#038589', borderBottom: '1px solid #DDE2F2', whiteSpace: 'nowrap' }}>Rev. Lead</th>
                                            <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, color: '#fb7a27', borderBottom: '1px solid #DDE2F2', whiteSpace: 'nowrap' }}>Investors</th>
                                            <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, color: '#0BA26F', borderBottom: '1px solid #DDE2F2', whiteSpace: 'nowrap' }}>Members</th>
                                            <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, color: '#AAACB5', borderBottom: '1px solid #DDE2F2', whiteSpace: 'nowrap' }}>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedRows.map((r, i) => (
                                            <tr key={r['companyId']} style={{ background: i % 2 === 0 ? '#fff' : '#F7F8FE', borderBottom: i < sortedRows.length - 1 ? '1px solid #DDE2F2' : 'none' }}>
                                                <td style={{ padding: '8px 12px', color: '#151313', fontWeight: 500 }}>
                                                    {r['cName'] || r['companyId']}
                                                    {r['testType'] && <span className="fs11px c28"> · {r['testType']} · {r['subscription']}</span>}
                                                </td>
                                                <td style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, color: '#5E5F66' }}>{r['stats']?.['advisors'] ?? '-'}</td>
                                                <td style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, color: '#5E5F66' }}>{r['stats']?.['revenuelead'] ?? '-'}</td>
                                                <td style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, color: '#5E5F66' }}>{r['stats']?.['investors'] ?? '-'}</td>
                                                <td style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, color: '#5E5F66' }}>{r['stats']?.['members'] ?? '-'}</td>
                                                <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                                                    <span style={{ background: `${STATUS_COLORS[r['status']]}1A`, color: STATUS_COLORS[r['status']], borderRadius: 50, padding: '2px 10px', fontSize: 11, fontWeight: 600, textTransform: 'capitalize' }}>
                                                        {r['status'] === 'running' ? 'Processing' : r['status']}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
